import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import PropertyCard from "../components/PropertyCard";
import PricingTiers from "../components/PricingTiers";

const HERO_IMG = "https://images.pexels.com/photos/31817160/pexels-photo-31817160.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=2000";

const CATEGORIES_FR = [
  {
    key: "sale",
    to: "/vente",
    title: "Vente immobilière",
    description: "Biens d'exception sélectionnés par nos conseillers.",
    img: "https://images.unsplash.com/photo-1758957530781-4ff54e09bee2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxwcmVtaXVtJTIwbWluaW1hbGlzdCUyMGFwYXJ0bWVudCUyMGludGVyaW9yJTIwbGl2aW5nJTIwcm9vbXxlbnwwfHx8fDE3NzgxMDY0MDh8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    key: "rental_long",
    to: "/location-longue",
    title: "Location longue durée",
    description: "Votre prochain chez-vous, pour 12 mois et plus.",
    img: "https://images.pexels.com/photos/31817157/pexels-photo-31817157.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    key: "rental_short",
    to: "/location-saisonniere",
    title: "Location saisonnière",
    description: "Séjours uniques, adresses privilégiées.",
    img: "https://images.unsplash.com/photo-1762117360871-f11fbad00ee1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjB2aWxsYSUyMGV4dGVyaW9yJTIwc3Vuc2V0fGVufDB8fHx8MTc3ODEwNjQwMHww&ixlib=rb-4.1.0&q=85",
  },
  {
    key: "consulting",
    to: "/conseil",
    title: "Cabinet de conseil",
    description: "Juridique, conseil, gestion. Expertise intégrée.",
    img: "https://images.unsplash.com/photo-1758518727820-28491c194bee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGNvbnN1bHRpbmclMjBtZWV0aW5nJTIwbW9kZXJuJTIwb2ZmaWNlfGVufDB8fHx8MTc3ODEwNjQwNXww&ixlib=rb-4.1.0&q=85",
  },
];

const CATEGORIES_EN = [
  { key: "sale", to: "/vente", title: "For sale", description: "Exceptional properties curated by our advisors.", img: CATEGORIES_FR[0].img },
  { key: "rental_long", to: "/location-longue", title: "Long-term rental", description: "Your next home, for 12 months or more.", img: CATEGORIES_FR[1].img },
  { key: "rental_short", to: "/location-saisonniere", title: "Short stays", description: "Unique stays, privileged addresses.", img: CATEGORIES_FR[2].img },
  { key: "consulting", to: "/conseil", title: "Consulting firm", description: "Legal, advisory, management. Integrated expertise.", img: CATEGORIES_FR[3].img },
];

export default function Home() {
  const { t, lang } = useI18n();
  const [featured, setFeatured] = useState([]);
  const categories = lang === "fr" ? CATEGORIES_FR : CATEGORIES_EN;

  useEffect(() => {
    api.get("/properties", { params: { featured: true, limit: 3 } }).then((r) => setFeatured(r.data));
  }, []);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden pt-16 pb-0 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-8">
          <div className="rise">
            <div className="eyebrow mb-6">{t.home.eyebrow}</div>
          </div>
          <h1 className="headline text-5xl sm:text-6xl lg:text-[96px] max-w-4xl text-[#1d1d1f] rise rise-delay-1">
            {t.home.title.split("\n").map((line, i) => (
              <span key={i} className="block">{line}</span>
            ))}
          </h1>
          <p className="text-[18px] sm:text-[21px] text-[#86868b] max-w-2xl mt-6 rise rise-delay-2">
            {t.home.subtitle}
          </p>
          <div className="flex gap-3 mt-10 rise rise-delay-3">
            <Link to="/vente" className="btn-pill btn-primary text-[14px]" data-testid="hero-cta-primary">
              {t.home.cta}
            </Link>
            <Link to="/conseil" className="btn-pill btn-secondary text-[14px]" data-testid="hero-cta-secondary">
              {t.home.ctaConsulting}
            </Link>
          </div>
        </div>

        <div className="mt-16 max-w-[1400px] mx-auto px-4">
          <div className="squircle relative aspect-[21/9] overflow-hidden bg-[#f5f5f7]">
            <img
              src={HERO_IMG}
              alt="Luxury villa"
              className="w-full h-full object-cover kenburns"
            />
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-32">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="eyebrow mb-3">{lang === "fr" ? "Nos métiers" : "Our disciplines"}</div>
              <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#1d1d1f]">
                {t.home.exploreTitle}
              </h2>
              <p className="text-[17px] text-[#86868b] mt-3 max-w-xl">{t.home.exploreSubtitle}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {categories.map((c, i) => (
              <Link
                to={c.to}
                key={c.key}
                className="squircle relative aspect-[4/3] overflow-hidden group block bg-[#f5f5f7] card-hover"
                data-testid={`category-card-${c.key}`}
              >
                <img
                  src={c.img}
                  alt={c.title}
                  className="w-full h-full object-cover transition-transform duration-1000 ease-[cubic-bezier(0.25,1,0.5,1)] group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/0 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <div className="flex items-end justify-between">
                    <div>
                      <h3 className="text-2xl sm:text-3xl font-medium tracking-tight mb-1">{c.title}</h3>
                      <p className="text-[14px] text-white/80 max-w-md">{c.description}</p>
                    </div>
                    <ArrowUpRight size={22} strokeWidth={1.5} className="transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="py-24 bg-[#f5f5f7]">
          <div className="max-w-[1200px] mx-auto px-6 sm:px-8">
            <div className="mb-12">
              <div className="eyebrow mb-3">{lang === "fr" ? "Sélection" : "Selection"}</div>
              <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#1d1d1f]">
                {t.home.featuredTitle}
              </h2>
              <p className="text-[17px] text-[#86868b] mt-3">{t.home.featuredSubtitle}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="featured-grid">
              {featured.map((p) => (
                <PropertyCard key={p.property_id} property={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PRICING */}
      <section className="py-32">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-8">
          <div className="text-center mb-16">
            <div className="eyebrow mb-3">{lang === "fr" ? "Abonnements agences" : "Agency plans"}</div>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#1d1d1f]">
              {t.home.plansTitle}
            </h2>
            <p className="text-[17px] text-[#86868b] mt-3 max-w-xl mx-auto">{t.home.plansSubtitle}</p>
          </div>
          <PricingTiers />
        </div>
      </section>
    </main>
  );
}
